/**
 * L1, the one-download law, machine-checked (ADR-020 §2/§7): all texture and
 * material authoring binds to ONE downloaded GLB, identified by content hash.
 * A master manifest names that hash; asking the master to serve any other
 * bytes REFUSES naming both hashes — separate downloads are separate
 * characters (measured: paired downloads of one prompt differ in
 * tessellation), and this refusal is the only thing standing between an
 * authored mask and the wrong mesh.
 *
 * SHA-256 rides WebCrypto (`crypto.subtle`) — present in every browser and
 * in Node ≥ 15, so the same binding check runs at the app door and in the
 * headless probe.
 * Depends on: zod, errors. Depended on by: the T2 probe; the app door when
 * authored masks land (T3).
 */
import { z } from 'zod';
import { TextureMasterError } from './errors.js';

// lib is pure ES2022 (no DOM); WebCrypto's digest is the one global we touch.
declare const crypto: {
  subtle: { digest(algorithm: string, data: Uint8Array | ArrayBuffer): Promise<ArrayBuffer> };
};

/**
 * The manifest a master store entry carries (the foxDude shape, 2026-08-01).
 * Loose on purpose: the store also records measured anatomy and export
 * settings — the BINDING contract needs only identity + hash.
 */
export const masterManifestSchema = z.looseObject({
  character: z.string().min(1),
  file: z.string().min(1),
  sha256: z.string().regex(/^[0-9a-f]{64}$/, 'sha256 must be 64 lowercase hex characters'),
});
export type MasterManifest = z.infer<typeof masterManifestSchema>;

export function parseMasterManifest(raw: unknown): MasterManifest {
  const parsed = masterManifestSchema.safeParse(raw);
  if (!parsed.success) {
    throw new TextureMasterError(
      parsed.error.issues.map((i) => `manifest ${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }
  return parsed.data;
}

export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * L1's gate: refuse serving GLB bytes the manifest does not name, with BOTH
 * hashes in the message — the caller learns which download they actually
 * hold, not just that something mismatched.
 */
export async function assertMasterBinding(manifest: MasterManifest, glb: ArrayBuffer | Uint8Array): Promise<void> {
  const actual = await sha256Hex(glb);
  if (actual !== manifest.sha256) {
    throw new TextureMasterError([
      `these bytes are not master "${manifest.character}" (${manifest.file}): the master is ` +
        `sha256 ${manifest.sha256}, the given GLB is ${actual} — a different download is a ` +
        `different character (ADR-020 L1); authoring cannot cross`,
    ]);
  }
}
